import React, { useMemo, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import TableContainer from "../../components/Common/TableContainer";
import { getClinic, deleteClinic, toggleClinicStatus, getClinicAll } from "service/api";
import { toast } from "react-toastify";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "reactstrap";

function DatatableTables() {
  const [alluserlist, setAllUsers] = useState({ users: [] });
  const [loading, setLoading] = useState(true);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedDeleteId, setSelectedDeleteId] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    getAllUsers();
  }, []);

  const getAllUsers = async () => {
    setLoading(true);
    try {
      const response = await getClinicAll();
      setAllUsers({ users: response.data });
    } catch (error) {
      console.log("Error while fetching data:", error);
      toast.error("Failed to fetch clinics");
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (id) => {
    setSelectedDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setSelectedDeleteId(null);
    setIsDeleteModalOpen(false);
  };

  const confirmDelete = async () => {
    try {
      await deleteClinic(selectedDeleteId);
      toast.success("Practice deleted successfully!");
      getAllUsers();
    } catch (error) {
      console.error("Error while deleting clinic:", error);
      toast.error("Failed to delete clinic");
    } finally {
      closeDeleteModal();
    }
  };

  const handleToggleStatus = async (clinicRow) => {
    try {
      await toggleClinicStatus(clinicRow._id);
      toast.success(`Practice ${clinicRow.is_active === 1 ? "deactivated" : "activated"} successfully!`);
      getAllUsers();
    } catch (error) {
      console.error("Error while toggling clinic status:", error);
      toast.error("Failed to update clinic status");
    }
  };

  const getClinicStatus = (clinicRow) => {
    if (typeof clinicRow?.is_active === "number") {
      return clinicRow.is_active;
    }

    return clinicRow?.guestpractice === 1 ? 0 : 1;
  };

  const columns = useMemo(
    () => [
      {
        Header: "Sr No.",
        Cell: ({ row }) => row.index + 1,
      },
      {
        Header: "Name",
        Cell: ({ row }) => {
          const logoFileName = row.original.logo
            ? row.original.logo.split(/[/\\]/).pop()
            : "";
          const logoPath = logoFileName
            ? `${process.env.REACT_APP_API_URL || "http://localhost:9000"}/uploads/${logoFileName}`
            : "";

          return (
            <div className="d-flex align-items-center gap-2">
              {logoPath ? (
                <img
                  src={logoPath}
                  alt={row.original.name}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    objectFit: "cover",
                    border: "1px solid #d9e2ec",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: "#eef3f7",
                    color: "#5c6b7a",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 16,
                    border: "1px solid #d9e2ec",
                    flexShrink: 0,
                  }}
                >
                  <i className="mdi mdi-office-building" />
                </div>
              )}
              <span>{row.original.name}</span>
            </div>
          );
        },
      },
      {
        Header: "Email",
        accessor: "email",
      },
      {
        Header: "Contact No.",
        accessor: "contactno",
      },
      {
        Header: "Ward",
        accessor: "ward",
        width: 90,
      },
      {
        Header: "Status",
        width: 110,
        Cell: ({ row }) => {
          const currentStatus = getClinicStatus(row.original);

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                whiteSpace: "nowrap",
              }}
            >
              <button
                className="btn btn-sm"
                title={currentStatus === 1 ? "Deactivate Practice" : "Activate Practice"}
                onClick={() => handleToggleStatus(row.original)}
                style={{
                  minWidth: "auto",
                  height: "auto",
                  border: "none",
                  background: "transparent",
                  boxShadow: "none",
                  color: currentStatus === 1 ? "#198754" : "#7c8794",
                  padding: 0,
                  margin: 0,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <i
                  className={`mdi ${currentStatus === 1 ? "mdi-toggle-switch" : "mdi-toggle-switch-off"}`}
                  style={{ fontSize: 34, lineHeight: 1 }}
                ></i>
              </button>
            </div>
          );
        },
      },
      {
        Header: "Action",
        width: 130,
        Cell: ({ row }) => {
          const id = row.original._id;
          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "nowrap",
                whiteSpace: "nowrap",
                minWidth: 110,
              }}
            >
              <button
                className="btn btn-sm"
                title="Manage Schedule"
                onClick={() => navigate(`/ClinicSchedule/${id}`)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: "1px solid #d8e3ea",
                  background: "#fff",
                  color: "#0d6efd",
                  padding: 0,
                  margin: 0,
                  flexShrink: 0,
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
                }}
              >
                <i className="mdi mdi-calendar-month-outline" style={{ fontSize: 18, lineHeight: 1 }}></i>
              </button>

              <button
                className="btn btn-sm"
                title="Edit"
                onClick={() => navigate(`/Editclinic/${id}`)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: "1px solid #d8e3ea",
                  background: "#fff",
                  color: "#3b82f6",
                  padding: 0,
                  margin: 0,
                  flexShrink: 0,
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
                }}
              >
                <i className="mdi mdi-pencil-outline" style={{ fontSize: 18, lineHeight: 1 }}></i>
              </button>

              <button
                className="btn btn-sm"
                title="Delete"
                onClick={() => openDeleteModal(id)}
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 10,
                  border: "1px solid #d8e3ea",
                  background: "#fff",
                  color: "#dc3545",
                  padding: 0,
                  margin: 0,
                  flexShrink: 0,
                  boxShadow: "0 4px 10px rgba(15, 23, 42, 0.06)",
                }}
              >
                <i className="mdi mdi-trash-can-outline" style={{ fontSize: 18, lineHeight: 1 }}></i>
              </button>
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  document.title = "Practice | List";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h3 className="page-title">Practice List</h3>
          <div className="d-flex gap-2">

            <Link to="/Addclinic" className="btn btn-primary">
              ADD NEW Practice
            </Link>
          </div>
        </div>
        {/* <Breadcrumbs title="Setting" breadcrumbItem="Practice" /> */}
        <TableContainer
          columns={columns}
          data={alluserlist.users}
          isGlobalFilter={true}
          isAddOptions={false}
          customPageSize={10}
          className="custom-header-css"
        />

        <Modal isOpen={isDeleteModalOpen} toggle={closeDeleteModal} centered>
          <ModalHeader toggle={closeDeleteModal}>Confirm Deletion</ModalHeader>
          <ModalBody>
            Are you sure you want to delete this Practice?
          </ModalBody>
          <ModalFooter>
            <Button color="danger" onClick={confirmDelete}>
              Yes, Delete
            </Button>
            <Button color="secondary" onClick={closeDeleteModal}>
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </div>
    </div>
  );
}

export default DatatableTables;
